// Página de perfil: progreso, estadísticas, historial y cuenta
import { useState, useEffect, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { useAuth } from '../hooks/useAuth';
import { TrophyIcon, ClockIcon } from '@heroicons/react/24/outline';
import { GAMES_BY_ID, type GameId } from '../games/catalog';
import GameIcon from '../components/GameIcon';
import { API_URL } from '../lib/api';
import { formatTime } from '../games/minesweeper/format';


// Cómo terminó una partida (el Snake no se gana ni se pierde: se muestra la puntuación)
function matchOutcome(match: { game_type: string; result: string | null; score: number | null }) {
  if (match.game_type === 'snake') return { label: `${match.score ?? 0} pts`, tone: 'bg-game-snake/10 text-ink' };
  if (match.result === 'win') return { label: 'Victoria', tone: 'bg-success/10 text-success' };
  return { label: 'Derrota', tone: 'bg-fill text-ink-2' };
}

// "Hoy, 21:50", "Ayer, 18:05" o "12 oct, 18:05" (con año si no es el actual)
function formatPlayedAt(iso: string) {
  const date = new Date(iso);
  const now = new Date();
  const time = date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (days === 0) return `Hoy, ${time}`;
  if (days === 1) return `Ayer, ${time}`;
  const day = date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
  return `${day.replace('.', '')}, ${time}`;
}

interface ProfileData {
  user: any;
  stats: any;
  achievements: any[]; // aún sin uso: los logros llegarán más adelante
  recentMatches: any[];
  snake?: { played: number; best: number; average: number };
}

function Section({ title, children, delay = 0 }: { title: string; children: ReactNode; delay?: number }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', bounce: 0, duration: 0.45, delay }}
    >
      <h2 className="px-1 mb-2 text-footnote font-semibold text-ink-2">{title}</h2>
      {children}
    </motion.section>
  );
}

const formatPlayTime = (s: number) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
};

export default function Profile() {
  const { user, updateLevel } = useAuthStore();
  const { logout } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [pwCurrent, setPwCurrent]   = useState('');
  const [pwNew, setPwNew]           = useState('');
  const [pwConfirm, setPwConfirm]   = useState('');
  const [pwLoading, setPwLoading]   = useState(false);
  const [pwError, setPwError]       = useState('');
  const [pwSuccess, setPwSuccess]   = useState(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwNew !== pwConfirm) { setPwError('Las contraseñas no coinciden'); return; }
    setPwLoading(true); setPwError(''); setPwSuccess(false);
    try {
      const token = localStorage.getItem('jugaly-auth')
        ? JSON.parse(localStorage.getItem('jugaly-auth')!).state?.token : null;
      await axios.post(`${API_URL}/api/auth/change-password`,
        { currentPassword: pwCurrent, newPassword: pwNew },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPwSuccess(true);
      setPwCurrent(''); setPwNew(''); setPwConfirm('');
    } catch (err: any) {
      setPwError(err.response?.data?.error ?? 'Error al cambiar la contraseña');
    } finally {
      setPwLoading(false);
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const [profileRes, levelRes] = await Promise.all([
          axios.get(`${API_URL}/api/users/me/profile`),
          user && !user.isGuest ? axios.get(`${API_URL}/api/users/me/level`) : Promise.resolve(null),
        ]);
        setProfile(profileRes.data.data);
        if (levelRes) {
          const d = levelRes.data.data;
          updateLevel({ xpAwarded: 0, totalXp: d.xp, currentLevel: d.currentLevel, nextLevelXp: d.nextLevelXp, currentLevelXp: d.currentLevelXp });
        }
      } catch (err) {
        console.error('Error al cargar perfil:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfile();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <div className="flex flex-col items-center gap-3">
          <span className="w-6 h-6 rounded-full border-2 border-ink/15 border-t-accent animate-spin" />
          <p className="text-footnote text-ink-2">Cargando perfil…</p>
        </div>
      </div>
    );
  }

  const stats = profile?.stats;
  const recentMatches = profile?.recentMatches || [];

  const initial = user?.username?.charAt(0).toUpperCase() || '?';
  const level = user?.level ?? 1;
  const xpInLevel = (user?.xp ?? 0) - (user?.currentLevelXp ?? 0);
  const xpForLevel = Math.max(1, (user?.nextLevelXp ?? 1) - (user?.currentLevelXp ?? 0));
  const progress = level >= 100 ? 1 : Math.max(0, Math.min(1, xpInLevel / xpForLevel));

  const statCells = stats ? [
    { label: 'Partidas', value: stats.gamesPlayed },
    { label: 'Victorias', value: stats.gamesWon },
    { label: 'Derrotas', value: stats.gamesLost },
    { label: 'Ganadas', value: `${stats.winRate} %` },
  ] : [];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-20 flex flex-col gap-8">
      {/* Cabecera */}
      <motion.header
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', bounce: 0, duration: 0.45 }}
        className="flex items-center gap-5"
      >
        <span className="w-[4.5rem] h-[4.5rem] shrink-0 rounded-full bg-accent text-accent-ink grid place-items-center text-title1 font-semibold">
          {initial}
        </span>
        <div className="flex-1 min-w-0">
          <h1 className="text-title1 font-bold text-ink truncate">{user?.username}</h1>
          <p className="text-footnote text-ink-2 truncate">
            {user?.isGuest ? 'Invitado' : user?.email}
            {!user?.isGuest && user?.createdAt && (
              <> · desde {new Date(user.createdAt).toLocaleDateString('es', { year: 'numeric', month: 'long' })}</>
            )}
          </p>
        </div>
      </motion.header>

      {/* Nivel */}
      {!user?.isGuest && (
        <Section title="Progreso" delay={0.05}>
          <div className="panel p-5 flex flex-col gap-4">
            <div className="flex items-baseline justify-between gap-4">
              <span className="text-title2 font-bold text-ink tabular">Nivel {level}</span>
              <span className="text-footnote text-ink-2 tabular">
                {level >= 100 ? 'Nivel máximo' : `${xpInLevel.toLocaleString('es-ES')} / ${xpForLevel.toLocaleString('es-ES')} XP`}
              </span>
            </div>
            <div className="h-2 rounded-full bg-fill overflow-hidden" role="progressbar" aria-valuenow={Math.round(progress * 100)} aria-valuemin={0} aria-valuemax={100} aria-label="Progreso hasta el siguiente nivel">
              <motion.div
                className="h-full rounded-full bg-accent origin-left"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: progress }}
                transition={{ type: 'spring', bounce: 0, duration: 0.8, delay: 0.15 }}
              />
            </div>
            <p className="flex items-center gap-2 text-footnote text-ink-2">
              <ClockIcon className="w-4 h-4" /> {formatPlayTime(user?.playTimeSeconds ?? 0)} de juego
            </p>
          </div>
        </Section>
      )}

      {/* Estadísticas */}
      <Section title="Buscaminas" delay={0.1}>
        {stats && stats.gamesPlayed > 0 ? (
          <div className="flex flex-col gap-3">
            <div className="rounded-2xl overflow-hidden grid grid-cols-2 sm:grid-cols-4 gap-px bg-line/60">
              {statCells.map((c) => (
                <div key={c.label} className="bg-surface px-5 py-4">
                  <div className="text-title2 font-bold text-ink tabular">{c.value ?? '—'}</div>
                  <div className="text-caption text-ink-2 mt-0.5">{c.label}</div>
                </div>
              ))}
            </div>
            <div className="panel flex items-center justify-between gap-4 px-5 h-12">
              <span className="text-body text-ink">Mejor tiempo</span>
              <span className={`text-body tabular ${stats.bestTime ? 'font-semibold text-ink' : 'text-ink-3'}`}>
                {stats.bestTime ? formatTime(stats.bestTime) : 'Sin récord'}
              </span>
            </div>
          </div>
        ) : (
          <div className="panel px-5 py-8 text-center text-footnote text-ink-2">Todavía no has terminado ninguna partida.</div>
        )}
      </Section>

      <Section title="Snake" delay={0.12}>
        {profile?.snake && profile.snake.played > 0 ? (
          <div className="rounded-2xl overflow-hidden grid grid-cols-3 gap-px bg-line/60">
            {[
              { label: 'Partidas', value: profile.snake.played },
              { label: 'Récord', value: profile.snake.best },
              { label: 'Media', value: profile.snake.average.toLocaleString('es-ES') },
            ].map((c) => (
              <div key={c.label} className="bg-surface px-5 py-4">
                <div className="text-title2 font-bold text-ink tabular">{c.value}</div>
                <div className="text-caption text-ink-2 mt-0.5">{c.label}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="panel px-5 py-8 text-center text-footnote text-ink-2">Todavía no has terminado ninguna partida.</div>
        )}
      </Section>

      {/* Historial */}
      <Section title="Partidas recientes" delay={0.15}>
        {recentMatches.length === 0 ? (
          <div className="panel px-5 py-8 text-center text-footnote text-ink-2">
            Todavía no hay partidas. Las que termines aparecerán aquí.
          </div>
        ) : (
          <ul className="panel divide-y divide-line/60 overflow-hidden">
            {recentMatches.map((match: any) => {
              const known = match.game_type in GAMES_BY_ID;
              const outcome = matchOutcome(match);
              const detail = [
                match.game_type === 'minesweeper' && match.result === 'win' && match.time_ms ? formatTime(match.time_ms) : null,
                match.game_type === 'snake' && match.time_ms ? `${Math.round(match.time_ms / 1000)} s de partida` : null,
              ].filter(Boolean).join(' · ');
              return (
                <li key={`${match.game_type}-${match.id}`} className="flex items-center gap-3.5 px-4 py-3">
                  {known ? <GameIcon id={match.game_type as GameId} size={36} /> : <span className="w-9 h-9 rounded-lg bg-fill" />}
                  <span className="flex-1 min-w-0">
                    <span className="block text-body text-ink truncate">{known ? GAMES_BY_ID[match.game_type as GameId].name : match.game_type}</span>
                    {detail && <span className="block text-caption text-ink-2 tabular">{detail}</span>}
                  </span>
                  <span className="flex flex-col items-end gap-1 shrink-0">
                    <span className={`px-2.5 h-6 inline-flex items-center rounded-full text-caption font-semibold ${outcome.tone}`}>
                      {outcome.label}
                    </span>
                    <time className="text-caption text-ink-2 tabular" dateTime={match.played_at}>{formatPlayedAt(match.played_at)}</time>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {/* Logros: todavía no disponibles */}
      <Section title="Logros" delay={0.2}>
        <div className="panel px-5 py-5 flex items-center gap-4">
          <span className="w-10 h-10 rounded-full bg-warning/10 text-warning grid place-items-center shrink-0">
            <TrophyIcon className="w-5 h-5" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-body font-semibold text-ink">Próximamente</p>
            <p className="text-footnote text-ink-2">Muy pronto podrás desbloquear logros jugando.</p>
          </div>
        </div>
      </Section>

      {/* Contraseña */}
      {!user?.isGuest && (
        <Section title="Cambiar contraseña" delay={0.25}>
          <form onSubmit={handleChangePassword} className="panel p-5 flex flex-col gap-4">
            {pwError && <p className="text-footnote text-danger" role="alert">{pwError}</p>}
            {pwSuccess && <p className="text-footnote text-success" role="status">Contraseña actualizada.</p>}
            {[
              { id: 'pw-current', label: 'Contraseña actual', value: pwCurrent, set: setPwCurrent, auto: 'current-password' },
              { id: 'pw-new', label: 'Nueva contraseña', value: pwNew, set: setPwNew, auto: 'new-password' },
              { id: 'pw-confirm', label: 'Repite la nueva contraseña', value: pwConfirm, set: setPwConfirm, auto: 'new-password' },
            ].map(({ id, label, value, set, auto }) => (
              <div key={id} className="max-w-sm">
                <label htmlFor={id} className="label">{label}</label>
                <input id={id} type="password" autoComplete={auto} className="input" value={value} onChange={(e) => set(e.target.value)} required minLength={id === 'pw-current' ? 1 : 6} />
              </div>
            ))}
            <button type="submit" disabled={pwLoading} className="btn-primary self-start">
              {pwLoading ? 'Guardando…' : 'Guardar contraseña'}
            </button>
          </form>
        </Section>
      )}

      <button onClick={logout} className="btn-secondary w-full bg-surface text-danger hover:bg-raised">Cerrar sesión</button>
    </div>
  );
}
